import React from 'react';
import * as yup from 'yup';
import { CreditsFormProps } from '@screens/players/player.actions/credits/types/credits.form.props';
import { MainDialogContent } from '@components/dialog/main.dialog.content';
import { Button, DialogActions, FormControl, FormHelperText, InputLabel } from '@material-ui/core';
import { useFormData } from '@domain/hooks/use.form.data';
import { ErrorDisplay } from '@components/error/error.display';
import { Loading } from '@components/loading';
import { CancelToken } from 'axios';
import { useAuth } from '@domain/auth/auth.hook';
import { BonusSelect } from '@screens/players/player.actions/credits/bonus.form.styles';
import { CenteredRoot, DescText } from '@styles/common';

const bonusPercents = [5, 10, 25, 50, 100, 200, 500];

const BonusFormSchema = yup.object({
  bonus: yup.number().safeRequired().oneOf(bonusPercents),
});

interface BonusFormData {
  bonus?: number;
}

export const BonusForm = ({ onFinish }: CreditsFormProps) => {
  const { authorizedServices } = useAuth();

  async function sendBonus(data: BonusFormData, cancelToken: CancelToken | undefined) {
    await BonusFormSchema.validate(data, { abortEarly: false });
    if (cancelToken) cancelToken.throwIfRequested();
    return authorizedServices.get('/admin/send-bonus', { params: data, cancelToken }).then(onFinish);
  }

  const formData = useFormData({}, sendBonus);

  const { data, loading, error, validationErrors, submit, handleChange } = formData;

  return (
    <>
      <MainDialogContent>
        <CenteredRoot>
          <Loading loading={loading} />

          <fieldset disabled={loading}>
            <DescText>Select purchase bonus percentage to send</DescText>

            <FormControl>
              <InputLabel id='bonus'>Bonus</InputLabel>
              <BonusSelect
                native
                labelId='bonus'
                id='bonus'
                value={data.bonus}
                inputProps={{ name: 'bonus' }}
                disabled={loading}
                onChange={handleChange}
                error={!!validationErrors?.bonus}
              >
                <option> </option>
                {bonusPercents.map((v) => (
                  <option key={v} value={v}>
                    {v}%
                  </option>
                ))}
              </BonusSelect>
              <FormHelperText error>{validationErrors?.bonus}</FormHelperText>
            </FormControl>
          </fieldset>

          <ErrorDisplay error={error} />
        </CenteredRoot>
      </MainDialogContent>

      <DialogActions>
        <Button onClick={submit} color='primary' disabled={loading}>
          Send
        </Button>
      </DialogActions>
    </>
  );
};
